<template>
  <div class="gl-order-page">

    <div class="container-fluid">
      <div class="gl-page-header">
        <div>
          <div class="gl-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <router-link to="/owner/orders">Orders</router-link>
            <span class="gl-bc-sep">/</span>
            <span>{{ order?.order_number || 'Details' }}</span>
          </div>
          <h1 class="gl-page-title">Order Details</h1>
        </div>
        <router-link to="/owner/orders" class="gl-back-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          <span>Back to Orders</span>
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-detail-grid">
          <div class="gl-main-col">
            <div class="gl-card gl-skel-card" style="height:220px"></div>
            <div class="gl-card gl-skel-card" style="height:280px"></div>
          </div>
          <div class="gl-side-col">
            <div class="gl-card gl-skel-card" style="height:120px"></div>
            <div class="gl-card gl-skel-card" style="height:160px"></div>
            <div class="gl-card gl-skel-card" style="height:140px"></div>
          </div>
        </div>
      </template>

      <!-- Loaded -->
      <template v-else-if="order">
        <div class="gl-detail-grid">

          <!-- ── LEFT: main ── -->
          <div class="gl-main-col">

            <!-- Status card -->
            <div class="gl-card">
              <div class="gl-order-hero">
                <div>
                  <span class="gl-order-num-lg">{{ order.order_number }}</span>
                  <span class="gl-order-dt">{{ formatDateTime(order.created_at) }}</span>
                </div>
                <span class="gl-status-pill" :class="getStatusClass(order.status)">{{ order.status.toUpperCase() }}</span>
              </div>

              <!-- Timeline -->
              <div class="gl-timeline-wrap">
                <div class="gl-tl-track"></div>
                <div class="gl-tl-steps">
                  <div class="gl-tl-step" :class="{ 'gl-tl-done': isStatusCompleted('pending'), 'gl-tl-active': isStatusActive('pending') }">
                    <div class="gl-tl-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <span class="gl-tl-lbl">Pending</span>
                    <span class="gl-tl-time">{{ order.created_at ? formatTime(order.created_at) : '' }}</span>
                  </div>
                  <div class="gl-tl-step" :class="{ 'gl-tl-done': isStatusCompleted('confirmed'), 'gl-tl-active': isStatusActive('confirmed') }">
                    <div class="gl-tl-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span class="gl-tl-lbl">Confirmed</span>
                    <span class="gl-tl-time">{{ order.confirmed_at ? formatTime(order.confirmed_at) : '' }}</span>
                  </div>
                  <div class="gl-tl-step" :class="{ 'gl-tl-done': isStatusCompleted('preparing'), 'gl-tl-active': isStatusActive('preparing') }">
                    <div class="gl-tl-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
                    </div>
                    <span class="gl-tl-lbl">Preparing</span>
                    <span class="gl-tl-time">{{ order.preparing_at ? formatTime(order.preparing_at) : '' }}</span>
                  </div>
                  <div class="gl-tl-step" :class="{ 'gl-tl-done': isStatusCompleted('ready'), 'gl-tl-active': isStatusActive('ready') }">
                    <div class="gl-tl-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                    </div>
                    <span class="gl-tl-lbl">Ready</span>
                    <span class="gl-tl-time">{{ order.ready_at ? formatTime(order.ready_at) : '' }}</span>
                  </div>
                  <div v-if="isDelivery" class="gl-tl-step" :class="{ 'gl-tl-done': isStatusCompleted('delivering'), 'gl-tl-active': isStatusActive('delivering') }">
                    <div class="gl-tl-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                    </div>
                    <span class="gl-tl-lbl">Delivering</span>
                    <span class="gl-tl-time">{{ order.delivering_at ? formatTime(order.delivering_at) : '' }}</span>
                  </div>
                  <div class="gl-tl-step" :class="{ 'gl-tl-done': isStatusCompleted('completed'), 'gl-tl-active': isStatusActive('completed') }">
                    <div class="gl-tl-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span class="gl-tl-lbl">Completed</span>
                    <span class="gl-tl-time">{{ order.completed_at ? formatTime(order.completed_at) : '' }}</span>
                  </div>
                </div>
              </div>

              <!-- Kitchen docket: available from the moment the order exists, and
                   still available after completion for reprints. -->
              <div v-if="order.status !== 'cancelled'" class="gl-action-row gl-action-row-print">
                <button class="gl-btn-kitchen" @click="openKitchenReceipt" :disabled="loadingKitchen">
                  <svg v-if="!loadingKitchen" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  <svg v-else class="gl-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  {{ order.kitchen_printed_at ? 'Reprint Kitchen Receipt' : 'Print Kitchen Receipt' }}
                </button>
                <small v-if="order.kitchen_printed_at" class="gl-printed-note">
                  Printed {{ formatTime(order.kitchen_printed_at) }}
                </small>
              </div>

              <!-- Actions -->
              <div v-if="order.status !== 'completed' && order.status !== 'cancelled'" class="gl-action-row">
                <button v-if="canProgress && nextStatus && !awaitingPayment" class="gl-btn-progress" @click="updateStatus(nextStatus)" :disabled="updating">
                  <svg v-if="!updating" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <svg v-else class="gl-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  Mark as {{ formatStatus(nextStatus) }}
                </button>
                <!-- Unpaid online order — cannot be progressed until payment clears -->
                <div v-if="awaitingPayment && canProgress" class="gl-pay-notice">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <small>Awaiting payment — this order can't be confirmed until payment is completed. If payment failed, cancel it.</small>
                </div>
                <!-- Rider-controlled phase notice -->
                <div v-if="isDelivery && ['ready', 'delivering'].includes(order.status)" class="gl-rider-notice">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <small>Delivery progress is controlled by the rider app — updates automatically.</small>
                </div>
                <button v-if="canCancel" class="gl-btn-cancel" @click="showCancelModal = true" :disabled="updating">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  Cancel Order
                </button>
              </div>

              <!-- Cancelled notice -->
              <div v-if="order.status === 'cancelled'" class="gl-cancelled-notice">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                <div>
                  <strong>Order Cancelled</strong>
                  <div v-if="order.cancel_reason" class="gl-cancel-reason">{{ order.cancel_reason }}</div>
                  <div v-if="order.cancelled_at" class="gl-cancel-time">{{ formatDateTime(order.cancelled_at) }}</div>
                </div>
              </div>
            </div>

            <!-- Order Items card -->
            <div class="gl-card">
              <div class="gl-card-header">Order Items</div>
              <div class="gl-items-wrap">
                <table class="gl-items-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th class="th-center">Qty</th>
                      <th class="th-right">Price</th>
                      <th class="th-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in order.items" :key="item.id">
                      <td>
                        <span class="gl-item-name">{{ item.product_name }}</span>
                        <span v-if="getOptionLabels(item.options).length" class="gl-item-opts">
                          {{ getOptionLabels(item.options).join(', ') }}
                        </span>
                        <span v-if="item.notes" class="gl-item-note">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                          {{ item.notes }}
                        </span>
                      </td>
                      <td class="td-center gl-item-qty">{{ item.quantity }}</td>
                      <td class="td-right gl-item-price">RM {{ formatPrice(item.unit_price) }}</td>
                      <td class="td-right gl-item-total">RM {{ formatPrice(item.total_price) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="gl-tfoot-row">
                      <td colspan="3" class="td-right gl-tfoot-lbl">Subtotal</td>
                      <td class="td-right gl-tfoot-val">RM {{ formatPrice(order.subtotal) }}</td>
                    </tr>
                    <tr v-if="order.discount > 0" class="gl-tfoot-row">
                      <td colspan="3" class="td-right gl-tfoot-lbl">Discount</td>
                      <td class="td-right gl-tfoot-val gl-tfoot-green">− RM {{ formatPrice(order.discount) }}</td>
                    </tr>
                    <tr v-if="order.points_discount > 0" class="gl-tfoot-row">
                      <td colspan="3" class="td-right gl-tfoot-lbl gl-tfoot-teal">Points Discount</td>
                      <td class="td-right gl-tfoot-val gl-tfoot-teal">− RM {{ formatPrice(order.points_discount) }}</td>
                    </tr>
                    <tr v-if="order.delivery_fee > 0" class="gl-tfoot-row">
                      <td colspan="3" class="td-right gl-tfoot-lbl">Delivery Fee</td>
                      <td class="td-right gl-tfoot-val">RM {{ formatPrice(order.delivery_fee) }}</td>
                    </tr>
                    <tr v-if="order.tax > 0" class="gl-tfoot-row">
                      <td colspan="3" class="td-right gl-tfoot-lbl">Tax</td>
                      <td class="td-right gl-tfoot-val">RM {{ formatPrice(order.tax) }}</td>
                    </tr>
                    <tr class="gl-tfoot-total">
                      <td colspan="3" class="td-right">Total</td>
                      <td class="td-right gl-total-val">RM {{ formatPrice(order.total) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- Delivery card -->
            <div v-if="order.type === 'delivery' && order.delivery" class="gl-card">
              <div class="gl-card-header">Delivery</div>
              <div class="gl-card-body">
                <div class="gl-delivery-grid">
                  <div>
                    <div class="gl-info-lbl">Delivery Address</div>
                    <div class="gl-info-val">{{ order.delivery_address }}</div>
                    <div v-if="order.delivery_notes" class="gl-info-note">{{ order.delivery_notes }}</div>
                  </div>
                  <div>
                    <div class="gl-info-lbl">Rider Status</div>
                    <span class="gl-delivery-badge" :class="getDeliveryClass(order.delivery.status)">
                      {{ (order.delivery.status || '').replace('_', ' ') }}
                    </span>

                    <!-- Rider assigned -->
                    <div v-if="order.delivery.rider" class="gl-rider-box">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      <div class="flex-grow-1">
                        <span class="gl-rider-name">{{ order.delivery.rider.name }}</span>
                        <span class="gl-rider-phone">
                          {{ order.delivery.rider.phone }}
                          <template v-if="order.delivery.rider.vehicle_plate"> · {{ order.delivery.rider.vehicle_plate }}</template>
                        </span>
                      </div>
                      <!-- Unassign only allowed before pickup -->
                      <button
                        v-if="order.delivery.status === 'assigned'"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="assigning"
                        @click="unassignRider"
                      >
                        Unassign
                      </button>
                    </div>

                    <!-- Assign rider (pending/failed) -->
                    <div v-else-if="['pending', 'failed'].includes(order.delivery.status)" class="mt-3">
                      <div v-if="order.status !== 'ready'" class="gl-dispatch-box">
                        Mark the order as Ready to assign a rider.
                      </div>
                      <div v-else>
                        <label class="gl-info-lbl mb-1">Assign internal rider</label>
                        <div class="d-flex gap-2">
                          <select v-model="selectedRiderId" class="form-select form-select-sm" :disabled="assigning || ridersLoading">
                            <option :value="null" disabled>
                              {{ ridersLoading ? 'Loading riders…' : (availableRiders.length ? 'Select a rider' : 'No riders at this outlet') }}
                            </option>
                            <option
                              v-for="r in availableRiders"
                              :key="r.id"
                              :value="r.id"
                              :disabled="!r.assignable"
                            >
                              {{ r.name }} ({{ r.status }}){{ r.has_active_job ? ' — busy' : '' }}
                            </option>
                          </select>
                          <button
                            class="btn btn-sm btn-primary text-nowrap"
                            :disabled="!selectedRiderId || assigning"
                            @click="assignRider"
                          >
                            <span v-if="assigning" class="spinner-border spinner-border-sm me-1"></span>
                            Assign
                          </button>
                        </div>
                        <div v-if="order.delivery.status === 'failed'" class="gl-info-note mt-1">
                          Previous dispatch failed — assign a rider manually.
                        </div>
                        <div v-if="assignError" class="gl-failed-box mt-2">{{ assignError }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- ── RIGHT: sidebar ── -->
          <div class="gl-side-col">

            <!-- Customer -->
            <div class="gl-card">
              <div class="gl-card-header">Customer</div>
              <div class="gl-card-body">
                <div class="gl-cust-row">
                  <div class="gl-cust-avatar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div>
                    <span class="gl-cust-name">{{ order.user?.name || 'Guest' }}</span>
                    <span class="gl-cust-phone">{{ order.user?.phone }}</span>
                  </div>
                </div>
                <div v-if="order.user?.email" class="gl-cust-email">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {{ order.user.email }}
                </div>
              </div>
            </div>

            <!-- Order Info -->
            <div class="gl-card">
              <div class="gl-card-header">Order Info</div>
              <div class="gl-meta-list">
                <div class="gl-meta-row">
                  <span class="gl-meta-lbl">Branch</span>
                  <span class="gl-meta-val">{{ order.outlet?.name }}</span>
                </div>
                <div class="gl-meta-row">
                  <span class="gl-meta-lbl">Order Type</span>
                  <span class="gl-type-badge" :class="getTypeClass(order.type)">{{ formatOrderType(order.type) }}</span>
                </div>
                <div v-if="order.table_number" class="gl-meta-row">
                  <span class="gl-meta-lbl">Table Number</span>
                  <span class="gl-meta-val">{{ order.table_number }}</span>
                </div>
                <div v-if="order.promo_code" class="gl-meta-row">
                  <span class="gl-meta-lbl">Promo Code</span>
                  <span class="gl-meta-code">{{ order.promo_code.code }}</span>
                </div>
                <div v-if="order.points_used > 0" class="gl-meta-row">
                  <span class="gl-meta-lbl">Points Used</span>
                  <span class="gl-meta-val">{{ order.points_used }} pts</span>
                </div>
                <div v-if="order.points_earned > 0" class="gl-meta-row">
                  <span class="gl-meta-lbl">Points Earned</span>
                  <span class="gl-meta-val gl-meta-green">+{{ order.points_earned }} pts</span>
                </div>
              </div>
            </div>

            <!-- Payment -->
            <div class="gl-card">
              <div class="gl-card-header">Payment</div>
              <div class="gl-meta-list">
                <div class="gl-meta-row">
                  <span class="gl-meta-lbl">Method</span>
                  <span class="gl-meta-val">{{ formatPaymentMethod(order.payment_method) }}</span>
                </div>
                <div class="gl-meta-row">
                  <span class="gl-meta-lbl">Status</span>
                  <span class="gl-pay-badge" :class="getPaymentClass(order.payment_status)">{{ order.payment_status }}</span>
                </div>
                <div v-if="order.payment_reference" class="gl-meta-row">
                  <span class="gl-meta-lbl">Reference</span>
                  <span class="gl-meta-code">{{ order.payment_reference }}</span>
                </div>
                <div v-if="order.paid_at" class="gl-meta-row">
                  <span class="gl-meta-lbl">Paid At</span>
                  <span class="gl-meta-val">{{ formatDateTime(order.paid_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Notes -->
            <div v-if="order.notes" class="gl-card">
              <div class="gl-card-header">Order Notes</div>
              <div class="gl-card-body gl-notes-body">{{ order.notes }}</div>
            </div>

          </div>
        </div>
      </template>

      <!-- Not found -->
      <div v-else class="gl-not-found">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <p>Order not found</p>
        <router-link to="/owner/orders" class="gl-back-btn">Back to Orders</router-link>
      </div>
    </div>

    <!-- Cancel Modal -->
    <div class="gl-modal-backdrop" v-if="showCancelModal" @click.self="showCancelModal = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-icon gl-modal-icon-red">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </div>
          <div>
            <h3 class="gl-modal-title">Cancel Order</h3>
            <p class="gl-modal-sub">This action cannot be undone.</p>
          </div>
          <button class="gl-modal-close" @click="showCancelModal = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <label class="gl-field-label">Reason for cancellation</label>
          <textarea class="gl-textarea" v-model="cancelReason" rows="3" placeholder="Enter reason…"></textarea>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-modal-cancel" @click="showCancelModal = false">Keep Order</button>
          <button class="gl-modal-confirm-red" @click="cancelOrder" :disabled="updating || !cancelReason">
            <svg v-if="updating" class="gl-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Yes, Cancel Order
          </button>
        </div>
      </div>
    </div>

    <!-- Kitchen docket preview / print -->
    <div class="gl-modal-backdrop" v-if="kitchenModalOpen" @click.self="kitchenModalOpen = false">
      <div class="gl-modal gl-modal-kitchen">
        <div class="gl-modal-header">
          <h3>Kitchen Receipt</h3>
          <button class="gl-modal-close" @click="kitchenModalOpen = false">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <!-- This block is what actually gets printed -->
          <div id="gl-kitchen-print" v-if="kitchenData">
            <div class="k-head">
              <div class="k-type">{{ kitchenData.type_label }}</div>
              <div class="k-order">{{ kitchenData.order_number }}</div>
              <div v-if="kitchenData.is_reprint" class="k-reprint">*** REPRINT ***</div>
            </div>

            <div class="k-meta">
              <div v-if="kitchenData.table_number"><strong>Table:</strong> {{ kitchenData.table_number }}</div>
              <div><strong>Placed:</strong> {{ kitchenData.placed_at }}</div>
              <div v-if="kitchenData.customer_name"><strong>Customer:</strong> {{ kitchenData.customer_name }}</div>
            </div>

            <div class="k-rule"></div>

            <div class="k-items">
              <div v-for="(item, i) in kitchenData.items" :key="i" class="k-item">
                <div class="k-item-main">
                  <span class="k-qty">{{ item.quantity }}x</span>
                  <span class="k-name">{{ item.name }}</span>
                </div>
                <div v-if="item.options && item.options.length" class="k-opts">
                  <div v-for="(o, oi) in item.options" :key="oi">+ {{ o }}</div>
                </div>
                <div v-if="item.notes" class="k-note">** {{ item.notes }} **</div>
              </div>
            </div>

            <div class="k-rule"></div>
            <div class="k-total">TOTAL ITEMS: {{ kitchenData.item_count }}</div>
            <div v-if="kitchenData.notes" class="k-order-note">
              <strong>Order note:</strong> {{ kitchenData.notes }}
            </div>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-modal-cancel" @click="kitchenModalOpen = false">Close</button>
          <button class="gl-modal-confirm" @click="printKitchenReceipt">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ownerOrderApi } from '@/services/ownerApi';

export default {
  name: 'OrderDetail',

  data() {
    return {
      loading: true,
      updating: false,
      order: null,
      showCancelModal: false,
      cancelReason: '',
      kitchenModalOpen: false,
      kitchenData: null,
      loadingKitchen: false,
      pollTimer: null,
      // Internal-rider assignment
      availableRiders: [],
      ridersLoading: false,
      selectedRiderId: null,
      assigning: false,
      assignError: '',
    };
  },

  computed: {
    isDelivery() {
      return this.order?.type === 'delivery';
    },
    canProgress() {
      // For delivery orders, the merchant only drives confirm → preparing → ready.
      // Once `ready`, dispatch + delivery progress is owned by the rider app.
      const merchantStages = this.isDelivery
        ? ['pending', 'confirmed', 'preparing']
        : ['pending', 'confirmed', 'preparing', 'ready'];
      return merchantStages.includes(this.order?.status);
    },
    awaitingPayment() {
      // Online/wallet (prepaid) orders must be paid before the merchant can
      // progress them. COD is paid on delivery, so it's exempt. Mirrors the
      // backend payment gate in Admin/Owner/OrderController::updateStatus.
      return this.order
        && this.order.payment_method !== 'cod'
        && this.order.payment_status !== 'paid';
    },
    canCancel() {
      // Block merchant cancellation once a rider has the food.
      const dStatus = this.order?.delivery?.status;
      if (this.isDelivery && ['picked_up', 'on_the_way', 'arrived', 'delivered'].includes(dStatus)) {
        return false;
      }
      return ['pending', 'confirmed', 'preparing', 'ready'].includes(this.order?.status);
    },
    nextStatus() {
      const transitions = {
        pending: 'confirmed', confirmed: 'preparing', preparing: 'ready',
        ready: this.isDelivery ? null : 'completed',
      };
      return transitions[this.order?.status];
    }
  },

  mounted() {
    this.fetchOrder();
  },

  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },

  methods: {
    async fetchOrder() {
      try {
        this.loading = true;
        const response = await ownerOrderApi.get(this.$route.params.id);
        this.order = response.data.data;
        this.syncPolling();
        this.maybeLoadRiders();
      } catch (error) {
        console.error('Failed to fetch order:', error);
        this.order = null;
      } finally {
        this.loading = false;
      }
    },

    // Whether the order needs the assign-rider dropdown populated.
    needsRiderAssignment() {
      return this.order?.type === 'delivery'
        && this.order?.status === 'ready'
        && this.order?.delivery
        && ['pending', 'failed'].includes(this.order.delivery.status)
        && !this.order.delivery.rider;
    },

    async maybeLoadRiders() {
      if (!this.needsRiderAssignment()) return;
      if (this.availableRiders.length || this.ridersLoading) return;
      try {
        this.ridersLoading = true;
        const { data } = await ownerOrderApi.availableRiders(this.order.id);
        this.availableRiders = data.data || [];
      } catch (e) {
        console.error('Failed to load riders:', e);
      } finally {
        this.ridersLoading = false;
      }
    },

    async assignRider() {
      if (!this.selectedRiderId) return;
      this.assigning = true;
      this.assignError = '';
      try {
        await ownerOrderApi.assignRider(this.order.id, this.selectedRiderId);
        this.selectedRiderId = null;
        this.availableRiders = [];
        await this.silentRefresh();
      } catch (e) {
        this.assignError = e.response?.data?.message || 'Failed to assign rider.';
      } finally {
        this.assigning = false;
      }
    },

    async unassignRider() {
      this.assigning = true;
      this.assignError = '';
      try {
        await ownerOrderApi.unassignRider(this.order.id);
        this.availableRiders = [];
        await this.silentRefresh();
        this.maybeLoadRiders();
      } catch (e) {
        this.assignError = e.response?.data?.message || 'Failed to unassign rider.';
      } finally {
        this.assigning = false;
      }
    },

    async silentRefresh() {
      try {
        const response = await ownerOrderApi.get(this.$route.params.id);
        this.order = response.data.data;
        this.syncPolling();
        this.maybeLoadRiders();
      } catch {}
    },

    syncPolling() {
      const s = this.order?.status;
      const delivery = this.order?.delivery;
      const active = this.order?.type === 'delivery'
        && ['ready', 'delivering'].includes(s)
        && delivery
        && !['delivered', 'cancelled'].includes(delivery.status);
      if (active && !this.pollTimer) {
        this.pollTimer = setInterval(() => this.silentRefresh(), 10000);
      } else if (!active && this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async openKitchenReceipt() {
      try {
        this.loadingKitchen = true;
        const response = await ownerOrderApi.kitchenReceipt(this.order.id);
        this.kitchenData = response.data.data;
        this.kitchenModalOpen = true;
      } catch (error) {
        console.error('Failed to load kitchen receipt:', error);
        alert(error.response?.data?.message || 'Failed to load kitchen receipt');
      } finally {
        this.loadingKitchen = false;
      }
    },

    async printKitchenReceipt() {
      const node = document.getElementById('gl-kitchen-print');
      if (!node) return;

      // Print in an isolated iframe so the dashboard's own styles (and dark mode)
      // can't bleed into the thermal docket.
      const frame = document.createElement('iframe');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      document.body.appendChild(frame);

      const doc = frame.contentWindow.document;
      doc.open();
      doc.write(`<html><head><title>Kitchen Receipt</title><style>
        @page { margin: 4mm; }
        body { font-family: 'Courier New', monospace; color: #000; background: #fff; width: 72mm; margin: 0; }
        .k-head { text-align: center; margin-bottom: 8px; }
        .k-type { font-size: 20px; font-weight: 700; letter-spacing: 1px; }
        .k-order { font-size: 15px; font-weight: 700; margin-top: 2px; }
        .k-reprint { font-size: 12px; font-weight: 700; margin-top: 4px; }
        .k-meta { font-size: 12px; line-height: 1.6; }
        .k-rule { border-top: 1px dashed #000; margin: 8px 0; }
        .k-item { margin-bottom: 9px; }
        .k-item-main { font-size: 15px; font-weight: 700; }
        .k-qty { display: inline-block; min-width: 26px; }
        .k-opts { font-size: 12px; padding-left: 26px; }
        .k-note { font-size: 12px; font-weight: 700; padding-left: 26px; }
        .k-total { font-size: 13px; font-weight: 700; text-align: center; }
        .k-order-note { font-size: 12px; margin-top: 8px; }
      </style></head><body>${node.innerHTML}</body></html>`);
      doc.close();

      frame.contentWindow.focus();
      frame.contentWindow.print();
      setTimeout(() => document.body.removeChild(frame), 1000);

      // Record the print, then refresh so the button reflects reprint state.
      try {
        await ownerOrderApi.markKitchenPrinted(this.order.id);
        await this.fetchOrder();
      } catch (error) {
        console.error('Failed to record kitchen print:', error);
      }

      this.kitchenModalOpen = false;
    },

    async updateStatus(status) {
      try {
        this.updating = true;
        const response = await ownerOrderApi.updateStatus(this.order.id, { status });
        this.order = response.data.data;
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to update order status');
      } finally {
        this.updating = false;
      }
    },

    async cancelOrder() {
      try {
        this.updating = true;
        const response = await ownerOrderApi.updateStatus(this.order.id, {
          status: 'cancelled', cancel_reason: this.cancelReason
        });
        this.order = response.data.data;
        this.showCancelModal = false;
        this.cancelReason = '';
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to cancel order');
      } finally {
        this.updating = false;
      }
    },

    formatPrice(amount) { return parseFloat(amount || 0).toFixed(2); },

    formatDateTime(d) {
      if (!d) return '';
      return new Date(d).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    formatTime(d) {
      if (!d) return '';
      return new Date(d).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' });
    },

    formatOrderType(type) {
      const map = { dine_in: 'Dine In', takeaway: 'Takeaway', delivery: 'Delivery' };
      return map[type] || type;
    },

    formatStatus(status) {
      if (!status) return '';
      return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    formatPaymentMethod(method) {
      if (!method) return '';
      const map = { cod: 'COD', fpx: 'FPX', card: 'Card', ewallet: 'eWallet', wallet: 'Wallet' };
      return map[method] || method;
    },

    parseOptions(options) {
      if (!options) return {};
      if (typeof options === 'string') { try { return JSON.parse(options); } catch { return {}; } }
      return options;
    },

    getOptionLabels(options) {
      const parsed = this.parseOptions(options);
      const labels = parsed?.option_labels;
      return Array.isArray(labels) ? labels : [];
    },

    isStatusActive(status) { return this.order?.status === status; },

    isStatusCompleted(status) {
      const order = ['pending', 'confirmed', 'preparing', 'ready', 'delivering', 'completed'];
      return order.indexOf(this.order?.status) > order.indexOf(status);
    },

    getStatusClass(status) {
      const map = {
        pending: 'gl-s-pending', confirmed: 'gl-s-confirmed', preparing: 'gl-s-preparing',
        ready: 'gl-s-ready', delivering: 'gl-s-delivering', completed: 'gl-s-completed', cancelled: 'gl-s-cancelled'
      };
      return map[status] || 'gl-s-default';
    },

    getTypeClass(type) {
      const map = { delivery: 'gl-t-delivery', takeaway: 'gl-t-takeaway', dine_in: 'gl-t-dine' };
      return map[type] || 'gl-t-default';
    },

    getPaymentClass(status) {
      const map = { paid: 'gl-pay-paid', pending: 'gl-pay-pending', failed: 'gl-pay-failed' };
      return map[status] || 'gl-pay-default';
    },

    getDeliveryClass(status) {
      const map = { pending: 'gl-del-pending', assigned: 'gl-del-assigned', picked_up: 'gl-del-picked', delivered: 'gl-del-done', cancelled: 'gl-del-cancelled' };
      return map[status] || 'gl-del-default';
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-order-page { padding-bottom: 40px; }
.gl-page-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }
.gl-back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border: 1.5px solid #e8eaf0; border-radius: 10px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  text-decoration: none; cursor: pointer; transition: all 0.15s;
}
.gl-back-btn:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-back-btn svg { transition: transform 0.15s; }
.gl-back-btn:hover svg { transform: translateX(-2px); }

/* ── Layout ── */
.gl-detail-grid { display: grid; grid-template-columns: 1fr 320px; gap: 18px; align-items: start; }
.gl-main-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.gl-side-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }

/* ── Card ── */
.gl-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px; overflow: hidden; animation: gl-fadein 0.3s ease; }
.gl-card-header { padding: 14px 20px; font-size: 13.5px; font-weight: 700; color: #111827; border-bottom: 1.5px solid #f5f6f8; }
.gl-card-body { padding: 16px 20px; }

/* ── Order hero ── */
.gl-order-hero {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 18px 20px 16px; border-bottom: 1.5px solid #f5f6f8; gap: 12px;
}
.gl-order-num-lg { display: block; font-family: 'SF Mono', 'Fira Code', monospace; font-size: 15px; font-weight: 700; color: #900C27; letter-spacing: 0.03em; }
.gl-order-dt { display: block; font-size: 12px; color: #9ca3af; margin-top: 2px; }

/* Status pill */
.gl-status-pill { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; letter-spacing: 0.05em; white-space: nowrap; flex-shrink: 0; }
.gl-s-pending   { background: #fef3c7; color: #d97706; }
.gl-s-confirmed { background: #e0f2fe; color: #0284c7; }
.gl-s-preparing { background: #dbeafe; color: #2563eb; }
.gl-s-ready     { background: #ede9fe; color: #7c3aed; }
.gl-s-delivering { background: #e0f2fe; color: #0369a1; }
.gl-s-completed { background: #dff0ec; color: #900C27; }
.gl-s-cancelled { background: #fee2e2; color: #dc2626; }
.gl-s-default   { background: #f3f4f6; color: #6b7280; }

/* ── Timeline ── */
.gl-timeline-wrap { padding: 20px 20px 4px; position: relative; }
.gl-tl-steps { display: flex; justify-content: space-between; position: relative; z-index: 1; }
.gl-tl-track {
  position: absolute; top: 34px; left: calc(20px + 15px); right: calc(20px + 15px);
  height: 2px; background: #f0f1f3; z-index: 0;
}
.gl-tl-step { display: flex; flex-direction: column; align-items: center; flex: 1; gap: 6px; padding-bottom: 16px; }
.gl-tl-icon {
  width: 30px; height: 30px; border-radius: 50%; background: #f0f1f3; color: #c0c4cc;
  display: flex; align-items: center; justify-content: center; position: relative; z-index: 1;
  transition: all 0.2s;
}
.gl-tl-lbl { font-size: 11px; font-weight: 600; color: #c0c4cc; white-space: nowrap; }
.gl-tl-time { font-size: 10px; color: #c0c4cc; white-space: nowrap; min-height: 14px; }

.gl-tl-done .gl-tl-icon { background: #dff0ec; color: #900C27; }
.gl-tl-done .gl-tl-lbl { color: #900C27; }
.gl-tl-done .gl-tl-time { color: #9ca3af; }
.gl-tl-active .gl-tl-icon { background: #900C27; color: #fff; box-shadow: 0 0 0 4px rgba(144,12,39,0.15); }
.gl-tl-active .gl-tl-lbl { color: #111827; font-weight: 700; }
.gl-tl-active .gl-tl-time { color: #6b7280; }

/* ── Action row ── */
.gl-action-row { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1.5px solid #f5f6f8; }
.gl-btn-progress {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; background: #900C27; color: #fff; border: none;
  border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.gl-btn-progress:hover:not(:disabled) { background: #1a5248; }
.gl-btn-progress:disabled { opacity: 0.6; cursor: not-allowed; }

/* Kitchen docket */
.gl-action-row-print { border-top: 1.5px solid #f5f6f8; }
.gl-btn-kitchen {
  display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 16px; border-radius: 9px;
  border: 1.5px solid #d1d5db; background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-btn-kitchen:hover:not(:disabled) { border-color: #900C27; color: #900C27; background: #fffafb; }
.gl-btn-kitchen:disabled { opacity: 0.6; cursor: not-allowed; }
.gl-printed-note { font-size: 11.5px; color: #9ca3af; }

/* Docket preview — mirrors the printed output */
.gl-modal-kitchen { max-width: 380px; }
#gl-kitchen-print { font-family: 'Courier New', monospace; color: #111; background: #fff; padding: 6px 4px; }
#gl-kitchen-print .k-head { text-align: center; margin-bottom: 10px; }
#gl-kitchen-print .k-type { font-size: 19px; font-weight: 700; letter-spacing: 1px; }
#gl-kitchen-print .k-order { font-size: 14px; font-weight: 700; margin-top: 2px; }
#gl-kitchen-print .k-reprint { font-size: 11.5px; font-weight: 700; margin-top: 4px; color: #900C27; }
#gl-kitchen-print .k-meta { font-size: 12px; line-height: 1.65; }
#gl-kitchen-print .k-rule { border-top: 1px dashed #999; margin: 9px 0; }
#gl-kitchen-print .k-item { margin-bottom: 9px; }
#gl-kitchen-print .k-item-main { font-size: 14.5px; font-weight: 700; }
#gl-kitchen-print .k-qty { display: inline-block; min-width: 26px; }
#gl-kitchen-print .k-opts { font-size: 12px; padding-left: 26px; color: #333; }
#gl-kitchen-print .k-note { font-size: 12px; font-weight: 700; padding-left: 26px; }
#gl-kitchen-print .k-total { font-size: 12.5px; font-weight: 700; text-align: center; }
#gl-kitchen-print .k-order-note { font-size: 12px; margin-top: 8px; }
.gl-btn-cancel {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; background: #fff; color: #dc2626; border: 1.5px solid #fecaca;
  border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.gl-btn-cancel:hover:not(:disabled) { background: #fee2e2; }
.gl-btn-cancel:disabled { opacity: 0.6; cursor: not-allowed; }

/* Awaiting-payment notice (replaces the progress button on unpaid prepaid orders) */
.gl-pay-notice {
  display: inline-flex; align-items: flex-start; gap: 8px;
  padding: 8px 14px; background: #fffbeb; border: 1.5px solid #fde68a;
  border-radius: 10px; color: #b45309; font-size: 12.5px; line-height: 1.4;
}
.gl-pay-notice svg { flex-shrink: 0; margin-top: 1px; }
body.dark-only .gl-pay-notice { background: #422006; border-color: #854d0e; color: #fbbf24; }

/* Cancelled notice */
.gl-cancelled-notice {
  display: flex; align-items: flex-start; gap: 10px;
  margin: 14px 20px; padding: 12px 14px; background: #fff5f5; border: 1.5px solid #fecaca;
  border-radius: 12px; color: #dc2626; font-size: 13px;
}
.gl-cancelled-notice strong { display: block; margin-bottom: 2px; }
.gl-cancel-reason { font-size: 12.5px; color: #374151; margin-top: 2px; }
.gl-cancel-time { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }

/* ── Items table ── */
.gl-items-wrap { overflow-x: auto; }
.gl-items-table { width: 100%; border-collapse: collapse; min-width: 380px; }
.gl-items-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-items-table thead th { padding: 10px 16px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; }
.gl-items-table thead th.th-center { text-align: center; }
.gl-items-table thead th.th-right { text-align: right; }
.gl-items-table tbody tr { border-bottom: 1px solid #f5f6f8; }
.gl-items-table tbody tr:last-child { border-bottom: none; }
.gl-items-table tbody td { padding: 12px 16px; vertical-align: top; }
.gl-items-table tbody td.td-center { text-align: center; }
.gl-items-table tbody td.td-right { text-align: right; }
.gl-item-name { display: block; font-size: 13.5px; font-weight: 600; color: #111827; }
.gl-item-opts { display: block; font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.gl-item-note { display: flex; align-items: center; gap: 4px; font-size: 11.5px; color: #9ca3af; font-style: italic; margin-top: 3px; }
.gl-item-qty { font-size: 13.5px; font-weight: 700; color: #374151; }
.gl-item-price { font-size: 13px; color: #6b7280; white-space: nowrap; }
.gl-item-total { font-size: 13.5px; font-weight: 600; color: #111827; white-space: nowrap; }

/* Totals footer */
.gl-items-table tfoot { border-top: 1.5px solid #f0f1f3; }
.gl-tfoot-row td { padding: 8px 16px; font-size: 13px; color: #6b7280; }
.gl-tfoot-lbl { }
.gl-tfoot-val { font-weight: 600; color: #374151; white-space: nowrap; }
.gl-tfoot-green { color: #16a34a !important; }
.gl-tfoot-teal { color: #900C27 !important; }
.gl-tfoot-total td { padding: 12px 16px; font-size: 15px; font-weight: 800; color: #111827; border-top: 1.5px solid #f0f1f3; }
.gl-total-val { color: #900C27; white-space: nowrap; }

/* ── Delivery card ── */
.gl-delivery-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.gl-info-lbl { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; margin-bottom: 6px; }
.gl-info-val { font-size: 13.5px; color: #374151; }
.gl-info-note { font-size: 12px; color: #9ca3af; font-style: italic; margin-top: 4px; }
.gl-delivery-badge { display: inline-block; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 600; margin-bottom: 10px; }
.gl-del-pending   { background: #fef3c7; color: #d97706; }
.gl-del-assigned  { background: #e0f2fe; color: #0284c7; }
.gl-del-picked    { background: #dbeafe; color: #2563eb; }
.gl-del-done      { background: #dff0ec; color: #900C27; }
.gl-del-cancelled { background: #fee2e2; color: #dc2626; }
.gl-del-default   { background: #f3f4f6; color: #6b7280; }
.gl-rider-box { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #f0f5f4; border-radius: 10px; }
.gl-rider-name { display: block; font-size: 13px; font-weight: 600; color: #111827; }
.gl-rider-phone { display: block; font-size: 11.5px; color: #6b7280; }
.gl-dispatch-box { display: flex; align-items: center; gap: 7px; padding: 8px 10px; background: #fffbeb; border-radius: 10px; font-size: 12.5px; color: #d97706; }
.gl-failed-box { display: flex; align-items: flex-start; gap: 7px; padding: 8px 10px; background: #fff5f5; border-radius: 10px; font-size: 12.5px; color: #dc2626; }

/* ── Sidebar cards ── */
.gl-cust-row { display: flex; align-items: center; gap: 12px; padding: 14px 20px 10px; }
.gl-cust-avatar {
  width: 40px; height: 40px; border-radius: 50%; background: #f0f5f4; color: #900C27;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-cust-name { display: block; font-size: 14px; font-weight: 700; color: #111827; }
.gl-cust-phone { display: block; font-size: 12px; color: #9ca3af; }
.gl-cust-email { display: flex; align-items: center; gap: 6px; padding: 0 20px 14px; font-size: 12.5px; color: #6b7280; }

.gl-meta-list { padding: 6px 0 8px; }
.gl-meta-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 20px; border-bottom: 1px solid #f9fafb; }
.gl-meta-row:last-child { border-bottom: none; }
.gl-meta-lbl { font-size: 12.5px; color: #9ca3af; font-weight: 500; }
.gl-meta-val { font-size: 13px; color: #374151; font-weight: 600; }
.gl-meta-green { color: #900C27 !important; }
.gl-meta-code { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px; background: #f3f4f6; padding: 2px 7px; border-radius: 5px; color: #374151; }

/* Type badges */
.gl-type-badge { display: inline-block; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 600; }
.gl-t-delivery { background: #eff6ff; color: #2563eb; }
.gl-t-takeaway { background: #f0fdf4; color: #16a34a; }
.gl-t-dine     { background: #fdf4ff; color: #9333ea; }
.gl-t-default  { background: #f3f4f6; color: #6b7280; }

/* Payment badges */
.gl-pay-badge { display: inline-block; padding: 3px 9px; border-radius: 6px; font-size: 11.5px; font-weight: 600; }
.gl-pay-paid    { background: #dff0ec; color: #900C27; }
.gl-pay-pending { background: #fef3c7; color: #d97706; }
.gl-pay-failed  { background: #fee2e2; color: #dc2626; }
.gl-pay-default { background: #f3f4f6; color: #6b7280; }

.gl-notes-body { font-size: 13.5px; color: #374151; line-height: 1.6; }

/* ── Skeleton ── */
.gl-skel-card { background: #f8f9fa; border-color: #f0f1f3; animation: gl-pulse 1.4s ease-in-out infinite; }

/* ── Not found ── */
.gl-not-found { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; padding: 60px; text-align: center; color: #9ca3af; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.gl-not-found p { font-size: 14px; font-weight: 600; margin: 0; }

/* ── Modal ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050; padding: 20px;
}
.gl-modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 440px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18); position: relative; overflow: hidden;
  animation: gl-fadein 0.2s ease;
}
.gl-modal-header { display: flex; align-items: flex-start; gap: 14px; padding: 20px 20px 16px; border-bottom: 1.5px solid #f5f6f8; }
.gl-modal-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-modal-icon-red { background: #fee2e2; color: #dc2626; }
.gl-modal-title { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 2px; }
.gl-modal-sub { font-size: 12.5px; color: #9ca3af; margin: 0; }
.gl-modal-close { position: absolute; top: 14px; right: 14px; width: 28px; height: 28px; border: none; background: #f3f4f6; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #6b7280; transition: background 0.15s; }
.gl-modal-close:hover { background: #e5e7eb; }
.gl-modal-body { padding: 16px 20px; }
.gl-field-label { display: block; font-size: 12.5px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.gl-textarea { width: 100%; border: 1.5px solid #e8eaf0; border-radius: 10px; padding: 9px 12px; font-size: 13.5px; color: #111827; outline: none; resize: vertical; transition: border-color 0.15s; box-sizing: border-box; }
.gl-textarea:focus { border-color: #900C27; }
.gl-modal-footer { display: flex; gap: 10px; padding: 14px 20px; border-top: 1.5px solid #f5f6f8; justify-content: flex-end; }
.gl-modal-cancel { padding: 8px 18px; border: 1.5px solid #e8eaf0; border-radius: 10px; background: #fff; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.gl-modal-cancel:hover { border-color: #d1d5db; background: #f9fafb; }
.gl-modal-confirm-red { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; border: none; border-radius: 10px; background: #dc2626; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
.gl-modal-confirm-red:hover:not(:disabled) { background: #b91c1c; }
.gl-modal-confirm-red:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page-header { padding: 14px 0 8px; gap: 8px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }
  .gl-back-btn span { display: none; }
  .gl-back-btn { padding: 7px 10px; }

  .gl-detail-grid { grid-template-columns: 1fr; gap: 12px; }

  /* Timeline: vertical layout on mobile */
  .gl-timeline-wrap { padding: 16px 16px 8px; }
  .gl-tl-steps { flex-direction: column; gap: 0; }
  .gl-tl-step { flex-direction: row; align-items: center; justify-content: flex-start; gap: 12px; padding: 9px 0; flex: unset; }
  .gl-tl-icon { width: 28px; height: 28px; flex-shrink: 0; }
  .gl-tl-lbl { font-size: 12.5px; font-weight: 600; flex: 1; text-align: left; white-space: normal; }
  .gl-tl-time { font-size: 11px; text-align: right; white-space: nowrap; min-height: unset; }
  /* Vertical connector line */
  .gl-tl-track { top: 23px; bottom: 23px; left: 29px; right: auto; width: 2px; height: auto; }

  .gl-order-hero { flex-wrap: wrap; gap: 8px; padding: 14px 16px 12px; }
  .gl-action-row { padding: 12px 16px; flex-wrap: wrap; }
  .gl-btn-progress, .gl-btn-cancel { font-size: 12.5px; padding: 8px 14px; }

  .gl-delivery-grid { grid-template-columns: 1fr; gap: 14px; }

  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; max-height: calc(100vh - 84px); overflow-y: auto; }
}

/* ── Animations ── */
@keyframes gl-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes gl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
.gl-spin { animation: gl-spin 0.8s linear infinite; }
@keyframes gl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>

<style>
/* Dark mode */
body.dark-only .gl-page-title { color: #f3f4f6; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #6b7280; }
body.dark-only .gl-back-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-back-btn:hover { border-color: #900C27; color: #a7c4be; background: #1a3330; }

body.dark-only .gl-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-card-header { color: #f9fafb; border-bottom-color: #374151; }

body.dark-only .gl-order-hero { border-bottom-color: #374151; }
body.dark-only .gl-order-num-lg { color: #a7c4be; }
body.dark-only .gl-order-dt { color: #6b7280; }

body.dark-only .gl-tl-track { background: #374151; }
body.dark-only .gl-tl-icon { background: #374151; color: #6b7280; }
body.dark-only .gl-tl-lbl { color: #6b7280; }
body.dark-only .gl-tl-time { color: #4b5563; }
body.dark-only .gl-tl-done .gl-tl-icon { background: #1a3330; color: #a7c4be; }
body.dark-only .gl-tl-done .gl-tl-lbl { color: #a7c4be; }
body.dark-only .gl-tl-done .gl-tl-time { color: #6b7280; }
body.dark-only .gl-tl-active .gl-tl-lbl { color: #f9fafb; }

body.dark-only .gl-action-row { border-top-color: #374151; }
body.dark-only .gl-btn-cancel { background: #1f2937; border-color: #7f1d1d; color: #f87171; }
body.dark-only .gl-btn-cancel:hover:not(:disabled) { background: #2a1010; }

body.dark-only .gl-cancelled-notice { background: #2a1010; border-color: #7f1d1d; }
body.dark-only .gl-cancel-reason { color: #d1d5db; }

body.dark-only .gl-items-table thead tr { background: #111827; }
body.dark-only .gl-items-table thead th { color: #6b7280; }
body.dark-only .gl-items-table tbody tr { border-bottom-color: #374151; }
body.dark-only .gl-items-table tfoot { border-top-color: #374151; }
body.dark-only .gl-item-name { color: #f9fafb; }
body.dark-only .gl-item-opts, body.dark-only .gl-item-note { color: #6b7280; }
body.dark-only .gl-item-price { color: #9ca3af; }
body.dark-only .gl-item-total { color: #f9fafb; }
body.dark-only .gl-tfoot-row td { color: #9ca3af; }
body.dark-only .gl-tfoot-val { color: #d1d5db; }
body.dark-only .gl-tfoot-total td { color: #f9fafb; border-top-color: #374151; }

body.dark-only .gl-cust-avatar { background: #1a3330; color: #a7c4be; }
body.dark-only .gl-cust-name { color: #f9fafb; }
body.dark-only .gl-cust-phone, body.dark-only .gl-cust-email { color: #6b7280; }

body.dark-only .gl-meta-row { border-bottom-color: #1f2937; }
body.dark-only .gl-meta-lbl { color: #6b7280; }
body.dark-only .gl-meta-val { color: #d1d5db; }
body.dark-only .gl-meta-code { background: #374151; color: #d1d5db; }

body.dark-only .gl-notes-body { color: #d1d5db; }

body.dark-only .gl-rider-box { background: #1a3330; }
body.dark-only .gl-rider-name { color: #f9fafb; }
body.dark-only .gl-rider-phone { color: #6b7280; }
body.dark-only .gl-dispatch-box { background: #2a2310; }
body.dark-only .gl-failed-box { background: #2a1010; }

body.dark-only .gl-info-val { color: #d1d5db; }

body.dark-only .gl-not-found { background: #1f2937; border-color: #374151; }
body.dark-only .gl-skel-card { background: #111827; border-color: #374151; }

body.dark-only .gl-modal { background: #1f2937; }
/* The docket preview represents printed paper — keep it white in dark mode too. */
body.dark-only #gl-kitchen-print { background: #fff !important; color: #111 !important; }
body.dark-only #gl-kitchen-print .k-opts { color: #333 !important; }
body.dark-only .gl-btn-kitchen { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-btn-kitchen:hover:not(:disabled) { border-color: #900C27 !important; color: #fff !important; background: rgba(144,12,39,0.2) !important; }
body.dark-only .gl-printed-note { color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-modal-header { border-bottom-color: #374151; }
body.dark-only .gl-modal-title { color: #f9fafb; }
body.dark-only .gl-modal-sub { color: #6b7280; }
body.dark-only .gl-modal-close { background: #374151; color: #9ca3af; }
body.dark-only .gl-modal-close:hover { background: #4b5563; }
body.dark-only .gl-modal-footer { border-top-color: #374151; }
body.dark-only .gl-modal-cancel { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-textarea { background: #111827; border-color: #374151; color: #f9fafb; }
body.dark-only .gl-field-label { color: #d1d5db; }
</style>
